// Reserved → Out, as a PAGE in the rental shell (design:
// backend/docs/DESIGN_CHECKIN_CHECKOUT_PAGES.md §1.1). The former CheckOutStep
// overlay, re-housed: the same three sections — verify items, contract,
// payment & deposits — side by side on a desk so the whole job is visible,
// stacked on a phone. Finish stays disabled until all three resolve, for the
// same three reasons the server refuses with (guardCheckoutPayload). Nothing
// is written until Finish; "← Back to order" discards the local ticks.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { UI, fmtMoney, useIsNarrow, STATUS_LABEL, STATUS_COLOR } from './pipeline';
import { NextUp } from './OrderActionContext';

const SD_LABEL = {
  pickup:             { collected: (a) => `Collected ${fmtMoney(a)} now`, waived: () => 'Waived for this order' },
  card_on_file:       { card_on_file: (a) => `Card on file confirmed for ${fmtMoney(a)}`, waived: () => 'Waived for this order' },
  charged_at_booking: { charged_at_booking: (a) => `Charged at booking (${fmtMoney(a)})`, waived: () => 'Waived for this order' },
};

const fmtD = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';

export default function CheckOutPage({ order, onBack, transition, onRefresh, openSign, openCollect, onDone }) {
  const narrow = useIsNarrow();          // ≤ 640: one column, fixed footer
  const mid = useIsNarrow(1023);         // ≤ 1023: two columns (items on top)
  const na = order.next_action || {};
  const d = na.dialog || {};
  const items = d.items || (order.rental_order_items || []).map(i => ({ id: i.id, item_name: i.item_name, quantity: i.quantity, variant_id: i.variant_id }));
  const [ticked, setTicked] = useState(() => new Set());
  const [sd, setSd] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [waitingSign, setWaitingSign] = useState(false);
  const rootRef = useRef(null);

  // The list row the operator clicked may have scrolled the shell (and the
  // document: the sidebar is taller than a short viewport). A page starts at
  // the top, whatever was scrolled before it.
  useEffect(() => {
    try { window.scrollTo(0, 0); } catch {}
    for (let el = rootRef.current?.parentElement; el; el = el.parentElement) if (el.scrollTop) el.scrollTop = 0;
  }, [order.id]);

  const signed = !!order.signed_at;
  const sdAmount = Number(d.security_deposit_amount || order.security_deposit_amount || 0);
  const collection = d.security_deposit_collection || 'pickup';
  const balance = Number(d.balance ?? Math.max(0, Number(order.total || 0) - Number(order.amount_paid || 0)));
  const depositDue = d.deposit_due;
  const paid = Number(d.amount_paid ?? order.amount_paid ?? 0);

  // No security deposit on the order → nothing to resolve.
  useEffect(() => { if (sdAmount === 0 && sd === null) setSd('none'); }, [sdAmount, sd]);
  // charged_at_booking is a fact, not a choice — preselect it.
  useEffect(() => { if (collection === 'charged_at_booking' && sdAmount > 0 && sd === null) setSd('charged_at_booking'); }, [collection, sdAmount, sd]);

  // While the signature window is open, poll the order every 3 s (the
  // postMessage from the sign page also refreshes; this covers a phone tab).
  useEffect(() => {
    if (!waitingSign || signed) return undefined;
    const t = setInterval(() => onRefresh && onRefresh(), 3000);
    return () => clearInterval(t);
  }, [waitingSign, signed, onRefresh]);

  const allTicked = items.length > 0 && items.every(i => ticked.has(String(i.id)));
  const finishReason = !allTicked ? 'Verify every item' : !signed ? 'Contract not signed' : !sd ? 'Resolve the deposit' : null;
  const sdOptions = useMemo(() => Object.entries(SD_LABEL[collection] || SD_LABEL.pickup), [collection]);
  // The label says what the server will actually do: text when a phone is on file, email when only an email is.
  const finishLabel = order.client_phone ? 'Check out & text client' : order.client_email ? 'Check out & email client' : 'Check out';

  async function finish() {
    setBusy(true); setErr(null);
    const r = await transition(order, { action: 'checkout', items_verified: items.map(i => String(i.id)), security_deposit: sd });
    setBusy(false);
    if (r.ok) onDone(r.order); else setErr(r.reason);
  }

  const unitLine = (i) => [i.serial_number ? `S/N ${i.serial_number}` : null, i.sku ? `SKU ${i.sku}` : null, i.variant_label || null].filter(Boolean).join(' · ');

  const section = (n, title, done, body, extra = {}) => (
    <div data-testid={`checkout-section-${n}`} style={{ border: `1px solid ${done ? '#1e4a35' : '#2a3050'}`, borderRadius: 10, overflow: 'hidden', background: '#252838', ...extra }}>
      <div style={{ padding: '12px 16px', background: done ? '#0c1a14' : '#1e2235', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ width: 24, height: 24, borderRadius: '50%', background: done ? '#14532d' : '#2e3150', color: done ? '#4ade80' : '#e2e8f0', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800 }}>{done ? '✓' : n}</span>
        <span style={{ fontWeight: 700, fontSize: 14, color: '#f1f5f9' }}>{title}</span>
      </div>
      <div style={{ padding: 16 }}>{body}</div>
    </div>
  );

  const sc = STATUS_COLOR[order.status] || STATUS_COLOR.reserved;
  const gridCols = narrow ? '1fr' : mid ? '1fr 1fr' : 'minmax(320px,1.3fr) minmax(280px,1fr) minmax(300px,1fr)';

  return (
    <div ref={rootRef} data-testid="checkout-step" data-page="checkout" style={{ maxWidth: 1400, margin: '0 auto', color: '#e2e8f0', paddingBottom: narrow ? 110 : 0, display: 'flex', flexDirection: 'column', minHeight: narrow ? undefined : 'calc(100vh - 56px)' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <button type="button" data-testid="page-back" onClick={onBack} style={{ background: 'none', border: 'none', color: '#f97316', cursor: 'pointer', fontSize: 13, fontWeight: 700, padding: 0 }}>← Back to order</button>
        <span style={{ color: '#333' }}>|</span>
        <span style={{ fontSize: 18, fontWeight: 800, color: '#fff' }}>Check Out {order.order_number || '#' + order.id}</span>
        <span data-testid="order-status-badge" style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>{STATUS_LABEL[order.status] || order.status}</span>
        <span style={{ fontSize: 12, color: '#94a3b8' }}>{order.client_name} · {fmtD(order.from_date)} → {fmtD(order.to_date)} · {items.length} item{items.length === 1 ? '' : 's'}</span>
        {!narrow && <span style={{ marginLeft: 'auto' }}><NextUp excludeId={order.id} /></span>}
      </div>
      {narrow && <div style={{ marginBottom: 12 }}><NextUp excludeId={order.id} narrow /></div>}

      {d.return_date_passed && (
        <div data-testid="return-date-passed" style={{ marginBottom: 14, padding: '10px 14px', background: 'rgba(251,191,36,0.08)', border: '1px solid #92400e', borderRadius: 8, fontSize: 12, color: '#fbbf24' }}>
          ⚠️ Return date {order.to_date} has already passed — this order will read <b>Overdue</b> the moment it is Out{`, and late-return fees (if the policy is on) count from that date.`}
        </div>
      )}

      {/* The three sections: side by side on a desk, stacked on a phone */}
      <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: narrow ? 12 : 20, alignItems: 'start' }}>
        {section(1, 'Verify items', allTicked, (
          <>
            {items.map(i => {
              const on = ticked.has(String(i.id));
              const sub = unitLine(i);
              return (
                <label key={i.id} data-testid="verify-item" style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', minHeight: 56, marginBottom: 8, borderRadius: 10, border: `1px solid ${on ? '#1e4a35' : '#2a3050'}`, background: on ? '#0c1a14' : '#181928', cursor: 'pointer', boxSizing: 'border-box' }}>
                  <input type="checkbox" checked={on} onChange={() => setTicked(prev => { const n = new Set(prev); on ? n.delete(String(i.id)) : n.add(String(i.id)); return n; })} style={{ width: 24, height: 24, flexShrink: 0 }} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 15, color: '#f1f5f9', fontWeight: 600 }}>{i.item_name}</span>
                    {sub && <span data-testid="item-unit" style={{ display: 'block', fontSize: 12, color: '#94a3b8', marginTop: 2, fontFamily: "'DM Mono','Courier New',monospace" }}>{sub}</span>}
                  </span>
                  <span style={{ fontSize: 13, color: '#94a3b8', whiteSpace: 'nowrap' }}>× {i.quantity || 1}</span>
                </label>
              );
            })}
            {items.length > 1 && !allTicked && <button type="button" onClick={() => setTicked(new Set(items.map(i => String(i.id))))} style={{ ...UI.cancelBtn, width: '100%', marginTop: 4, padding: 12 }}>All present</button>}
          </>
        ), mid && !narrow ? { gridColumn: '1 / -1' } : {})}

        {section(2, 'Contract', signed, signed
          ? <div style={{ fontSize: 13, color: '#4ade80' }}>✓ Signed {new Date(order.signed_at).toLocaleString()}{order.signer_name ? ` by ${order.signer_name}` : ''}</div>
          : <>
              <div style={{ fontSize: 13, color: '#e2e8f0', marginBottom: 12, lineHeight: 1.5 }}>The rental agreement has not been signed. Capture it now — the client signs on this device or on the second screen, and this page updates by itself.</div>
              <button type="button" data-testid="capture-signature" onClick={() => { setWaitingSign(true); openSign(order); }} style={{ ...UI.goBtn(true, '#7c2d12', '#fb923c'), width: '100%', padding: 14 }}>✍️ Capture signature</button>
              {waitingSign && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 10 }}>Waiting for the signature…</div>}
            </>
        )}

        {section(3, 'Payment & deposits', !!sd, (
          <>
            {/* Money position from the ledger (order.amount_paid), re-read after every collect. */}
            {balance <= 0
              ? <div data-testid="paid-in-full" style={{ ...UI.row, color: '#4ade80', fontWeight: 700 }}><span>✓ Paid in full</span><span>{fmtMoney(paid)} of {fmtMoney(order.total)}</span></div>
              : <>
                  <div data-testid="booking-deposit" style={UI.row}>
                    <span>Booking deposit</span>
                    <span style={{ color: depositDue != null && paid + 0.005 >= depositDue ? '#4ade80' : '#e2e8f0' }}>
                      {depositDue == null ? '—' : paid + 0.005 >= depositDue ? `✓ satisfied (${fmtMoney(depositDue)})` : `${fmtMoney(depositDue)} due`}
                    </span>
                  </div>
                  <div style={UI.row}><span>Paid so far</span><span style={{ color: '#4ade80' }}>{fmtMoney(paid)}</span></div>
                  <div data-testid="balance-line" style={{ ...UI.row, fontWeight: 800, color: '#f87171', fontSize: 14 }}><span>Balance</span><span>{fmtMoney(balance)}</span></div>
                  {openCollect && (() => {
                    // From the deposit line: start at the unpaid part of the booking deposit; the full balance is one tap away in the modal.
                    const depositLeft = depositDue != null ? Math.max(0, Math.round((Number(depositDue) - paid) * 100) / 100) : 0;
                    const start = depositLeft > 0 ? Math.min(depositLeft, balance) : balance;
                    return <button type="button" data-testid="step-collect" onClick={() => openCollect(order, { defaultAmount: start })} style={{ ...UI.cancelBtn, width: '100%', marginTop: 10, padding: 12, color: '#4ade80' }}>💰 Collect {depositLeft > 0 ? `deposit ${fmtMoney(start)}` : fmtMoney(balance)}</button>;
                  })()}
                </>}
            <label style={UI.lbl}>Security deposit {sdAmount > 0 ? fmtMoney(sdAmount) : '— none on this order'}</label>
            {sdAmount > 0
              ? sdOptions.map(([key, label]) => (
                  <label key={key} data-testid={`sd-${key}`} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', minHeight: 48, marginBottom: 8, borderRadius: 10, border: `1px solid ${sd === key ? '#1e4a35' : '#2a3050'}`, background: sd === key ? '#0c1a14' : '#181928', cursor: 'pointer', fontSize: 13, color: '#f1f5f9', boxSizing: 'border-box' }}>
                    <input type="radio" name="sd" checked={sd === key} onChange={() => setSd(key)} style={{ width: 20, height: 20 }} /> {label(sdAmount)}
                  </label>
                ))
              : <div style={{ fontSize: 12, color: '#94a3b8' }}>Nothing to collect.</div>}
          </>
        ))}
      </div>

      {err && <div style={{ marginTop: 12, color: '#f87171', fontSize: 12 }} data-testid="dialog-error">{err}</div>}

      {/* Footer: what the server will do, and the one button. Sticky on a desk, fixed on a phone. */}
      <div data-testid="checkout-footer" style={narrow
        ? { position: 'fixed', left: 0, right: 0, bottom: 0, padding: '10px 12px calc(10px + env(safe-area-inset-bottom))', background: 'rgba(30,34,53,0.97)', borderTop: '1px solid #2a3050', zIndex: 1500, display: 'flex', flexDirection: 'column', gap: 8 }
        : { position: 'sticky', bottom: 0, marginTop: 'auto', paddingTop: 14, padding: '14px 0 16px', background: '#1e2235', borderTop: '1px solid #2a3050', display: 'flex', alignItems: 'center', gap: 20, zIndex: 5 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {!narrow && (na.effects || []).map((line, i) => <div key={i} style={{ ...UI.effect, padding: '1px 0' }}>• {line}</div>)}
        </div>
        <button data-testid="checkout-finish" disabled={busy || !!finishReason} onClick={finish} title={finishReason || ''} style={{ ...UI.goBtn(!busy && !finishReason), flex: 'none', width: narrow ? '100%' : 'auto', minWidth: 280, padding: '14px 24px', fontSize: 15 }}>
          {busy ? 'Checking out…' : finishReason ? finishReason : finishLabel}
        </button>
      </div>
    </div>
  );
}
